import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';

import { MessageService } from 'primeng/api';
// eslint-disable-next-line @typescript-eslint/naming-convention
import GPT3Tokenizer from 'gpt3-tokenizer';

import { AIRefinementApiModel } from '../../../../api/ai/model/AI-refinement-api.model';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { copy } from '../../../Misc/helpers/copy/copy.helper';
import { htmlToText } from '../../../Misc/helpers/html/html.helper';

import { AbstractAiDialogComponent } from '../AbstractAIDialog/Abstract-AI-dialog.component';
import { ScrollPanelComponent } from '../../../Basic/components/ScrollPanel/scroll-panel.component';
import { AIChatCompletionApiModel, AIChatCompletionMessageApiModel } from '../../../../api/ai/model/AI-chat-completion-api.model';
import { ChatMessageTypeModel, ChatRole } from '../../../../api/ai/enums/AI-chat-completion.enum';

@Component({
    selector: 'agpr-ai-refinement-dialog',
    templateUrl: './AI-refinement-dialog.component.html',
    styleUrls: ['./AI-refinement-dialog.component.scss'],
    standalone: false
})
export class AIRefinementDialogComponent extends AbstractAiDialogComponent implements OnInit {

    @Input() public selectionHtml: string;
    @Input() public preSelectionHtml: string;
    @Input() public postSelectionHtml: string;
    @Input() public prompt: string;
    @Input() public aiReferenceId: string;
    @Input() public hideInsert: boolean = false;

    readonly buttonTypes = ButtonTypesEnum;

    tokenizer = new GPT3Tokenizer({ type: 'gpt3' });

    loading: boolean = false;
    hasGenerated: boolean = true;
    generatedContent: string = '';
    originalTextCollapsed: boolean = true;
    streamingStarted: boolean = false;
    agreedOriginalText: boolean = false;
    tryToSubmit: boolean = false;
    showError: boolean = false;
    selectedPrompt: string;

    encodedSelectionHtml: { bpe: number[]; text: string[] };
    encodedPreSelectionHtml: { bpe: number[]; text: string[] };
    encodedPostSelectionHtml: { bpe: number[]; text: string[] };
    encodedPrompt: { bpe: number[]; text: string[] };

    readonly maxTokenAllowed: number = 1800;
    readonly maxTokenBackendLimit: number = 2047;
    readonly templateTokenSize: number = 120;
    tokenPromptSent: number = 0;

    @ViewChild('panelOriginalTextToggle') public panelOriginalTextToggle: ElementRef;
    @ViewChild('sp') scrollPanel: ScrollPanelComponent;

    @Input() positionY: number = 0;
    @Input() positionX: number = 0;

    reader: ReadableStreamDefaultReader<Uint8Array>;

    constructor(
        public coreDialogService: CoreDialogService,
        public configService: ConfigService,
        public messageService: MessageService,
        public localizationService: LocalizationService
    ) {
        super(coreDialogService, 'AIRefinement');
    }

    ngOnInit(): void {
        if (this.prompt) {
            this.generate();
        }
    }

    submitRefinementPrompt(prompt: string) {
        this.prompt = this.localizationService.transform(prompt);
        this.tryToSubmit = false;
        if (this.prompt) {
            this.generate();
        } else {
            this.tryToSubmit = true;
        }
    }

    clearSelectedPrompt() {
        this.selectedPrompt = '';
    }

    generate(): void {
        this.generatedContent = '';
        this.loading = true;
        this.hasGenerated = false;
        this.streamingStarted = true;
        this.showError = false;
        this.clearSelectedPrompt();

        requestAnimationFrame(() => {
            const content = document.querySelector('#generated_content');
            const listener = new ResizeObserver((entries) => this.scrollToBottom(entries[0]));
            if (listener && content) {
                listener.observe(content);
            }

            this.fetchData(this.configService.getConfig().apiUrl + '/ai/chat/completions', undefined, this.aiReferenceId);
        });
    }

    scrollToBottom(entry: ResizeObserverEntry) {
        if (this.scrollPanel?.contentDoesVerticalScroll()) {
            this.scrollPanel.scrollTo(entry.contentRect.bottom);
        }
    }

    handleError() {
        if (this.tokenPromptSent >= this.maxTokenBackendLimit) {
            this.messageService.add(
                {
                    severity: messageSeverityEnum.error,
                    summary: 'Common.dialog.aiRefinement.error.tokenLimit.header',
                    detail: this.localizationService.transform(
                        'Common.dialog.aiRefinement.error.limitExceeded'
                    ),
                }
            );
        } else {
            this.messageService.add(
                {
                    severity: messageSeverityEnum.error,
                    summary: 'Common.dialog.aiHelper.error.title',
                    detail: 'Common.dialog.aiHelper.error.description'
                }
            );
        }

        this.loading = false;
        this.hasGenerated = true;
        this.showError = true;
    }

    getRequestModel(): AIChatCompletionApiModel {
        const message: AIChatCompletionMessageApiModel = {
            role: ChatRole.USER,
            chatMessageType: ChatMessageTypeModel.CONTENT_REFINEMENT,
            message : JSON.stringify(this.getRequestMessage())
        };

        return { messages: [ message ] };
    }

    getRequestMessage(): AIRefinementApiModel {
        // the max tokens per prompt we can have is 4097 tokens shared between prompt and completion
        // so assuming the logic that the prompt & completion can be split 50/50
        // our template is about 105 tokens
        // so my logic is if prompt + selection + pre selection + post selection is less than maxTokenAllowed (1800 tokens), to include it all, else
        // just include prompt & selection
        this.encodedSelectionHtml = this.tokenizer.encode(this.selectionHtml || '');
        this.encodedPreSelectionHtml = this.tokenizer.encode(this.preSelectionHtml || '');
        this.encodedPostSelectionHtml = this.tokenizer.encode(this.postSelectionHtml || '');
        this.encodedPrompt = this.tokenizer.encode(this.prompt || '');

        let preSelectionHtml = null, postSelectionHtml = null;

        // because we strip out the "double html elements" with the pre & post selections.. we cannot just "not" include them in the
        // if (
        //     (this.encodedSelectionHtml.text.length +
        //         this.encodedPreSelectionHtml.text.length +
        //         this.encodedPostSelectionHtml.text.length +
        //         this.encodedPrompt.text.length
        //     ) <= this.maxTokenAllowed
        // ) {
        //     preSelectionHtml = this.preSelectionHtml;
        //     postSelectionHtml = this.postSelectionHtml;
        // }
        preSelectionHtml = this.preSelectionHtml;
        postSelectionHtml = this.postSelectionHtml;

        this.tokenPromptSent = this.tokenizer.encode(this.selectionHtml || '')?.text?.length +
            this.tokenizer.encode(preSelectionHtml || '')?.text?.length +
            this.tokenizer.encode(postSelectionHtml || '')?.text?.length +
            this.tokenizer.encode(this.prompt || '')?.text?.length + this.templateTokenSize;

        return {
            preContext: preSelectionHtml,
            postContext: postSelectionHtml,
            selectionText: this.selectionHtml,
            instruction: this.prompt
        };
    }

    setOriginalTextToggle(event) {
        if (event) {
            // Give the panel time to animate collapse before removing content
            setTimeout(() => {
                this.originalTextCollapsed = event;
            }, 400);
        } else {
            this.originalTextCollapsed = event;
        }
    }

    changeOriginalText(value: boolean) {
        this.agreedOriginalText = value;
    }

    stopStreaming() {
        this.streamingStarted = false;

        this.reader?.cancel();
    }

    copyGeneratedContent() {
        copy.text(htmlToText(this.generatedContent));

        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: 'Common.dialog.aiRefinement.copy.label',
            detail: 'Common.dialog.aiRefinement.copy.message',
        });
    }

    replace() {
        this.closeDialog({
            action: 'replace',
            generatedContent: this.generatedContent
        });
    }

    insert() {
        this.closeDialog({
            action: 'insert',
            generatedContent: this.generatedContent
        });
    }

    cancel(): void {
        this.reader?.cancel();
        this.closeDialog();
    }
}
